/**
 * Step 1: Basic Information
 *
 * Collects dataset name, description, format, and type
 */

import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDatasetBasicInfo } from '@/features/datasets/store/datasetCreationStore';
import { validateDatasetName } from '@/features/datasets/utils/datasetValidation';
import { DATASET_FORMATS, DATASET_TYPES } from '@/features/datasets/types/dataset.types';
import type { DatasetFormat, DatasetType } from '@/features/datasets/types/dataset.types';

/**
 * Basic Info Step Component
 */
export const DatasetBasicInfoStep: React.FC = () => {
  const {
    name,
    description,
    format,
    type,
    setName,
    setDescription,
    setFormat,
    setType,
  } = useDatasetBasicInfo();

  // Validate name
  const nameErrors = validateDatasetName(name);
  const hasNameError = name.length > 0 && nameErrors.length > 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
        <p className="text-muted-foreground text-sm">
          Provide the essential details for your dataset.
        </p>
      </div>

      {/* Dataset Name */}
      <div className="space-y-2">
        <Label htmlFor="dataset-name" className="required">
          Dataset Name
        </Label>
        <Input
          id="dataset-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., customer_transactions"
          className={hasNameError ? 'border-destructive' : ''}
          maxLength={255}
        />
        {hasNameError && (
          <p className="text-sm text-destructive">{nameErrors[0].message}</p>
        )}
        <p className="text-xs text-muted-foreground">
          Must start with a letter. Only letters, numbers, and underscores allowed ({name.length}/255 characters)
        </p>
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="dataset-description">
          Description
        </Label>
        <Textarea
          id="dataset-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe the purpose and contents of this dataset..."
          rows={4}
          maxLength={500}
        />
        <p className="text-xs text-muted-foreground">
          {description.length}/500 characters (optional)
        </p>
      </div>

      {/* Format */}
      <div className="space-y-2">
        <Label htmlFor="dataset-format" className="required">
          Format
        </Label>
        <Select value={format} onValueChange={(value) => setFormat(value as DatasetFormat)}>
          <SelectTrigger id="dataset-format">
            <SelectValue placeholder="Select format" />
          </SelectTrigger>
          <SelectContent>
            {DATASET_FORMATS.map((fmt) => (
              <SelectItem key={fmt.value} value={fmt.value}>
                {fmt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Choose the file format for storing your dataset
        </p>
      </div>

      {/* Type */}
      <div className="space-y-2">
        <Label htmlFor="dataset-type" className="required">
          Type
        </Label>
        <Select value={type} onValueChange={(value) => setType(value as DatasetType)}>
          <SelectTrigger id="dataset-type">
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            {DATASET_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                <div className="flex flex-col items-start">
                  <span className="font-medium">{t.label}</span>
                  <span className="text-xs text-muted-foreground">{t.description}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Define how this dataset is managed
        </p>
      </div>
    </div>
  );
};
